import React, { useState, useMemo, useEffect } from "react";
import { makeStyles, useTheme } from "@mui/styles";

import Grid from "@mui/material/Grid";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import Typography from "@mui/material/Typography";
import PaymentTypes from "./PaymentTypes";
import PaymentTypesByDate from "./PaymentTypesByDate";
import PaymentTypesByTable from "./PaymentTypesByTable";

import { showErrorSnackbar } from "store/actions/snackbarMessage";
import { useDispatch } from "react-redux";
import Button from "@mui/material/Button";
import API from "util/API";
import moment from "moment";
import LinearProgress from "@mui/material/LinearProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { useQuery } from "@tanstack/react-query";
import CircularProgress from "@mui/material/CircularProgress";


const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(2),
  },
  table: {},
  row: {
    height: "42px",
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(1),
  },
  dateTimeContainer: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(1),
  },
  dateTimePicker: {
    width: '100%',
  },
  appStoreContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    flexDirection: 'column',
    alignItems: 'center',
  },
  appPaper: {
    padding: theme.spacing(3),
    maxWidth: 600,
    width: '100%',
    backgroundColor: theme.palette.background.default,
    boxShadow: theme.shadows[2],
    borderRadius: theme.shape.borderRadius * 2,
  },
  appTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
  appIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  appDescription: {
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
  storeLinksContainer: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  storeLink: {
    display: "inline-block",
    overflow: "hidden",
    borderRadius: "13px",
    margin: theme.spacing(1),
    transition: 'transform 0.3s ease-in-out',
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
  appleImage: {
    borderRadius: "13px",
    width: "180px",
    height: "60px",
  },
  googleImage: {
    width: "180px",
    height: "auto",
  },
}));

const PaymentTypesReport = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  
  // Initialize dates in useEffect to prevent hydration mismatch
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dailyReport, setDailyReport] = useState(false);
  const [groupByTable, setGroupByTable] = useState(false);
  const [queryEnabled, setQueryEnabled] = useState(false);

  // Set initial dates after component mounts to prevent hydration issues
  useEffect(() => {
    const now = new Date();
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    
    setStartDate(startOfDay);
    setEndDate(now);
  }, []);

  

  
  const showError = (msg) => dispatch(showErrorSnackbar(msg));

  const changeDailyReport = (event) => {
    setDailyReport(event.target.checked);
    setQueryEnabled(false);
  };

  const changeGroupByTable = (event) => {
    setGroupByTable(event.target.checked);
    setQueryEnabled(false);
  };

  const formateDate = (date) => {
    return moment(date).format("YYYY-MM-DD HH:mm:ss");
  };

  const validateDates = () => {
    // check if dates are initialized
    if (!startDate || !endDate) {
      showError("Please wait for dates to initialize");
      return false;
    }
    // check start date and end date
    if (startDate > endDate) {
      showError("Start date must be before end date");
      return false;
    }
    // check start date and end date is future date
    if (startDate > new Date() || endDate > new Date()) {
      showError("Start date and end date must be before today");
      return false;
    }
    // check start date and end date gap is less than 32 days
    if (endDate - startDate > 32 * 24 * 60 * 60 * 1000) {
      showError("Start date and end date gap must be less than 32 days");
      return false;
    }
    
    return true;
  };

  const fetchReportData = async () => {
    if (!startDate || !endDate) {
      throw new Error("Dates not initialized");
    }
    const startDateStr = formateDate(startDate);
    const endDateStr = formateDate(endDate);
    const response = await API.get(
      `/paymentTypesReport?startDateStr=${startDateStr}&endDateStr=${endDateStr}&dailyReport=${dailyReport}&groupByTable=${groupByTable}`
    );
    return response.data;
  };
  
  const { data: reportData, isLoading, error } = useQuery({
    queryKey: ['paymentTypesReport', startDate, endDate, dailyReport, groupByTable],
    queryFn: fetchReportData,
    enabled: queryEnabled,
    onError: (err) => {
      console.log(err);
      showError("Something went wrong, please try again.");
    }
  });
  
  const queryDate = () => {
    if (validateDates()) {
      setQueryEnabled(true);
    }
  };

  // const syncPOSData = async () => {
  //   try {
  //     const endDateStr = formateDate(endDate);
  //     const response = await API.post(
  //       `/uploadPOSTransactions`,
  //       {
  //         transactionDateStr: endDateStr,
  //       }
  //     );
  //     return response.data;
  //   } catch (err) {
  //     console.log(err);
  //     showError("Something went wrong, please try again.");
  //     throw err;
  //   }
  // };
  
  

  // Show loading while dates are initializing
  if (!startDate || !endDate) {
    return (
      <div className={classes.root}>
        <LinearProgress style={{ marginBottom: 8, marginTop: 8 }} />
        <Typography variant="h6" style={{ textAlign: 'center', marginTop: 16 }}>
          Initializing...
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {isLoading ? (
        <LinearProgress style={{ marginBottom: 8, marginTop: 8 }} />
      ) : null}

      <Grid container>
        <Grid
          item
          lg={12}
          md={12}
          xl={12}
          xs={12}
          container
          direction="row"
          alignItems="center"
        >
          <div className={classes.dateTimeContainer}>
            <Typography style={{ marginRight: theme.spacing(1) }}>
              Start Date
            </Typography>
            <DatePicker
              selected={startDate}
              onChange={(date) => {
                setStartDate(date);
                setQueryEnabled(false);
              }}
              dateFormat="dd/MM/yyyy HH:mm"
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              className={classes.dateTimePicker}
            />
          </div>
          
          <div className={classes.dateTimeContainer}>
            <Typography style={{ marginRight: theme.spacing(1) }}>
              End Date
            </Typography>
            <DatePicker
              selected={endDate}
              onChange={(date) => {
                setEndDate(date);
                setQueryEnabled(false);
              }}
              dateFormat="dd/MM/yyyy HH:mm"
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              className={classes.dateTimePicker}
            />
          </div>

          <FormControlLabel
            style={{ marginLeft: theme.spacing(1) }}
            control={
              <Checkbox
                checked={dailyReport}
                onChange={(e) => changeDailyReport(e)}
                name="dailyReport"
                color="primary"
              />
            }
            label="Daily Report"
          />

          <FormControlLabel
            style={{ marginLeft: theme.spacing(1) }}
            control={
              <Checkbox
                checked={groupByTable}
                onChange={(e) => changeGroupByTable(e)}
                name="groupByTable"
                color="primary"
              />
            }
            label="Group by Table"
          />

          <Button
            color="primary"
            variant="contained"
            style={{ marginLeft: theme.spacing(1) }}
            onClick={(e) => queryDate()}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" style={{ marginRight: theme.spacing(1) }} />
            ) : null}
            Query
          </Button>

          {/* <Button
            color="primary"
            variant="outlined"
            style={{ marginLeft: theme.spacing(1) }}
            onClick={(e) => syncPOSData()}
            disabled={isLoading}
          >
            Sync POS End Date Data
          </Button> */}
        </Grid>
        


        <Grid item md={12} lg={8} xl={6} xs={12}>
          {dailyReport ? (
            <PaymentTypesByDate 
              reportData={reportData || []} 
              startDate={startDate}
              endDate={endDate} 
              isLoading={isLoading}
            />
          ) : groupByTable ? (
            <PaymentTypesByTable
              reportData={reportData || []}
              startDate={startDate}
              endDate={endDate}
              isLoading={isLoading}
            />
          ) : (
            <PaymentTypes
              startDate={startDate}
              endDate={endDate}
              reportData={reportData || []}
              isLoading={isLoading}
            />
          )}
        </Grid>
      </Grid>
    </div>
  );
};

export default PaymentTypesReport;
